<template>
  <b-modal
    :id="id"
    :ref="modalRef"
    centered
    scrollable
    :title="`Share Campaign Group: ${ group }`"
    modal-class="share-modal"
    data-test="creative-share-modal"
  >
    <div v-if="shareType === 'CAMPAIGN'">
      <p>Important: Sharing the Campaign Group allows other users to edit all associated campaigns.</p>
    </div>
    <div
      class="border rounded p-4 mb-3"
    >
      <label
        for="user-email-input"
        class="font-weight-bold fn-size-15"
      >
        Add User
      </label>
      <b-input-group>
        <b-form-input
          id="user-email-input"
          placeholder="Email Address"
          v-model="userEmail"
          type="email"
          :class="[!emailInSharedWithList ? '' : 'is-invalid']"
        />
        <template #append>
          <b-button
            data-test="add-user-email"
            variant="transparent"
            @click="addEmailToShareList"
            :disabled="isAddDisabled"
            :class="{ 'btn-loading black-loader': isAddButtonLoading }"
          >
            Add
          </b-button>
        </template>
      </b-input-group>
      <div
        style="font-size: 0.75rem;"
        class="mt-2"
      >
        <p
          v-if="emailInSharedWithList && !isAddButtonLoading"
          :class="{'invalid': emailInSharedWithList}"
        >
          The entered email is already in the "Share With" list below.
        </p>
        <p
          v-else-if="!emailInSharedWithList && !isAddButtonLoading"
        >
          Enter the email addresses of the people you wish to share the campaign group with.
        </p>
      </div>
    </div>
    <div
      v-if="isShareWithEmailsListLoading"
      class="loader mx-auto"
      style="height: 300px"
    />
    <div
      class="border rounded p-4"
      v-else-if="alreadySharedWithEmails.length > 0 || totalSharedWithEmails.length > 0"
    >
      <p
        class="font-weight-bold fn-size-15"
      >
        Share With
      </p>
      <div
        id="share-with-emails-list-container"
      >
        <ul
          id="share-with-emails-list"
          class="m-0 p-0"
        >
          <share-group-modal-emails-list-item
            v-for="(email, index) in totalSharedWithEmails"
            class="border share-with-emails-item"
            :class="{
              'rounded': totalSharedWithEmails.length === 1,
              'top-card': totalSharedWithEmails.length > 1 && index === 0,
              'bottom-card': totalSharedWithEmails.length > 1 && index === totalSharedWithEmails.length -1,
              'borderless-bottom': totalSharedWithEmails.length > 1 && index !== totalSharedWithEmails.length -1,
            }"
            :key="index"
            :index="index"
            :resend-enabled="alreadySharedWithEmails.includes(email)"
            :advertiser-id="advertiserId"
            :share-type="shareType"
            :group="group"
            :email="email"
            @removeEmail="removeEmail"
          />
        </ul>
      </div>
    </div>
    <template #modal-footer>
      <b-button
        id="share-modal-invite"
        variant="primary"
        size="sm"
        type="button"
        @click="inviteEmails($event)"
        :disabled="isInviteDisabled"
        :class="{ 'btn-loading': isInviteButtonLoading }"
      >
        Share
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import signUtils from '@/core/signUtils';
import { mapActions, mapState } from 'vuex';
import * as shareTypes from '@/modules/share/store/types';

import ShareGroupModalEmailsListItem from '@/modules/share/components/ShareGroupModalEmailsListItem';

function initialState() {
  return {
    newUserForm: {
      firstName: '',
      lastName: '',
      companyName: '',
    },
    isShareWithEmailsListLoading: false,
    emailInSharedWithList: false,
    isAddButtonLoading: false,
    isInviteButtonLoading: false,
    userEmail: '',
    alreadySharedWithEmails: [],
    userShareList: [],
  };
}

export default {
  name: 'ShareModal',
  components: {
    ShareGroupModalEmailsListItem,
  },
  props: {
    id: {
      type: String,
      default: 'modalSharing',
    },
    shareType: {
      type: String,
      default: '',
    },
    advertiserId: {
      type: Number,
      default: 0,
    },
    group: {
      type: String,
      default: '',
    },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState('content', [
      'creative',
    ]),
    ...mapState('account', {
      sharedByFirstName: 'firstName',
      sharedByLastName: 'lastName',
    }),
    isAddDisabled() {
      return !this.isEmailValid() || this.isAddButtonLoading;
    },
    isInviteDisabled() {
      return this.userShareList.length === 0 || this.isInviteButtonLoading;
    },
    totalSharedWithEmails() {
      return [...this.userShareList, ...this.alreadySharedWithEmails];
    },
    modalRef() {
      return `shareCampaignGroupModal-${this.id}`;
    },
  },
  methods: {
    ...mapActions('share', [
      shareTypes.FETCH_GROUP_SHARES,
      shareTypes.UPDATE_GROUP_SHARES,
      shareTypes.REMOVE_GROUP_SHARE_FOR_EMAIL,
    ]),
    openModal() {
      this.loadData();
      this.$bvModal.show(this.id);
    },
    loadData() {
      this.getAlreadySharedWithEmails();
    },
    resetData() {
      Object.assign(this.$data, initialState());
    },
    trimEmail() {
      this.userEmail = this.userEmail.trim();
    },
    isEmailValid() {
      return signUtils.isEmailValid(this.userEmail);
    },
    async getAlreadySharedWithEmails() {
      this.isShareWithEmailsListLoading = true;
      this.alreadySharedWithEmails = await this.FETCH_GROUP_SHARES({ advertiserId: this.advertiserId, group: this.group, shareType: this.shareType });
      this.isShareWithEmailsListLoading = false;
    },
    addEmailToShareList() {
      this.isAddButtonLoading = true;
      this.trimEmail();
      if (this.totalSharedWithEmails.includes(this.userEmail)) {
        this.emailInSharedWithList = true;
        this.isAddButtonLoading = false;
        return;
      }

      this.userShareList.push(this.userEmail);
      this.emailInSharedWithList = false;
      this.userEmail = '';
      this.isAddButtonLoading = false;
    },
    removeEmail(email) {
      if (this.alreadySharedWithEmails.includes(email)) {
        this.REMOVE_GROUP_SHARE_FOR_EMAIL({
          advertiserId: this.advertiserId,
          group: this.group,
          body: {
            type: this.shareType,
            sharedWithEmail: email,
          },
        })
          .then(() => {
            const index = this.alreadySharedWithEmails.findIndex((existingEmail) => existingEmail === email);
            this.alreadySharedWithEmails.splice(index, 1);
          });
        return;
      }
      if (this.userShareList.includes(email)) {
        const index = this.userShareList.findIndex((existingEmail) => existingEmail === email);
        this.userShareList.splice(index, 1);
      }
    },
    async inviteEmails(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.isInviteButtonLoading = true;

      this.UPDATE_GROUP_SHARES({
        advertiserId: this.advertiserId,
        group: this.group,
        body: {
          type: this.shareType,
          sharedWithEmails: [...this.userShareList],
        },
      })
        .then(() => {
          [...this.userShareList].forEach((email) => {
            this.alreadySharedWithEmails.push(email);
          });
          this.userShareList = [];
        })
        .finally(() => {
          this.isInviteButtonLoading = false;
        });
    },
    showModal() {
      this.loadData();
      this.$refs[this.modalRef].show();
    },
    hideModal() {
      this.$refs[this.modalRef].hide();
    },
  },
  mounted() {
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId === this.id) {
        this.resetData();
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
    this.$root.$off('bv::modal::hidden');
  },
};
</script>
<style scoped lang="scss">
.fn-size-15 {
font-size: 15px;
}
.black-loader::after {
  border: 2px solid #000;
}
#share-with-emails-list-container {
  min-height: 300px;
  max-height: 40vh;
  overflow: scroll;
}
.borderless-bottom {
  border-bottom: 0 solid transparent !important;
}
.top-card {
border-radius: 4px 4px 0 0;
margin: 0;
border-bottom: 0 solid transparent;
}
.bottom-card {
border-radius: 0 0 4px 4px;
margin: 0;
border-top: 0 solid transparent;
}
</style>
<style lang="scss">
.share-modal .modal-dialog {
    min-width: 600px;
}
</style>
